import React, { RefObject, useMemo, useRef } from 'react';
import Table from 'src/components/Table';
import { ITableColumn } from 'src/components/Table/types';
import {
  createKeyPickupSuiteTableColumns,
  EnrichedSuite,
  enrichSuitesForKeyPickup,
} from './helpers';
import { toast } from 'react-toastify';
import { ISuite } from 'src/components/SharedTypes';
import './BulkKeyPickupSuiteTable.scss';

interface BulkKeyPickupsSuiteTableProps {
  suites: ISuite[];
  selectedSuiteIds: string[];
  onSuiteSelect: (suiteId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

const BulkKeyPickupsSuiteTable = ({
  suites,
  selectedSuiteIds,
  onSuiteSelect,
  onSelectAll,
  onDeselectAll,
}: BulkKeyPickupsSuiteTableProps) => {
  const tableRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const eligibleSuites: ISuite[] = useMemo(() => {
    return suites.filter((suite: ISuite) => suite.isKeyHandOff === false);
  }, [suites]);

  const hasAvailableSuites: boolean = eligibleSuites.length > 0;

  const isAllEligibleSelected: boolean = useMemo(() => {
    return (
      eligibleSuites.length > 0 &&
      eligibleSuites.every((suite: ISuite) =>
        selectedSuiteIds.includes(suite.id)
      )
    );
  }, [eligibleSuites, selectedSuiteIds]);

  const handleSelectAll = () => {
    if (isAllEligibleSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  const handleSuiteSelect = (suiteId: string) => {
    const suite: ISuite = suites.find((s: ISuite) => s.id === suiteId);

    if (!suite) {
      toast.error(
        `Suite with ID ${suiteId} not found. Please refresh the page and try again.`
      );
      console.error(`handleSuiteSelect: Suite not found for ID: ${suiteId}`);
      return;
    }

    const isAvailableForKeyPickup: boolean = suite.isKeyHandOff === false;

    if (!isAvailableForKeyPickup) {
      toast.warning(
        `Suite ${suite.number} is not available for key pickup. Key has already been handed off.`
      );
      return console.warn(
        `handleSuiteSelect: Attempted to select unavailable suite: ${suite.number} (ID: ${suiteId})`
      );
    }

    if (!suiteId || suiteId.trim() === '') {
      toast.error('Invalid suite selection. Please try again.');
      console.error('handleSuiteSelect: Invalid suiteId provided');
      return;
    }

    onSuiteSelect(suiteId);
  };

  const { columns, enrichedSuites } = useMemo(() => {
    if (!hasAvailableSuites) {
      return { columns: [], enrichedSuites: [] };
    }

    const enrichedSuites: EnrichedSuite[] = enrichSuitesForKeyPickup(
      suites,
      selectedSuiteIds
    );

    const columns: ITableColumn[] = createKeyPickupSuiteTableColumns({
      allEligibleSelected: isAllEligibleSelected,
      onSelectAll: handleSelectAll,
      onSuiteSelect: handleSuiteSelect,
    });

    return {
      columns,
      enrichedSuites,
    };
  }, [isAllEligibleSelected, selectedSuiteIds, suites]);

  return (
    <div className="suite-table">
      {suites.length === 0 ? (
        <div className="empty-state">
          <p>No suites available for this property.</p>
        </div>
      ) : !hasAvailableSuites ? (
        <div className="empty-state">
          <p>
            No suites available for key pickup. All keys have been handed off.
          </p>
        </div>
      ) : (
        <div ref={tableRef}>
          <Table
            applyMinWidth
            defaultSort={{
              accessor: 'number',
              direction: 'ascending',
            }}
            columns={columns}
            data={enrichedSuites}
            headerTop={undefined}
          />
        </div>
      )}
    </div>
  );
};

export default BulkKeyPickupsSuiteTable;
